import { AnimatePresence, easeIn, easeOut, motion } from 'framer-motion'
import React from 'react'
import { useEventListener } from 'usehooks-ts'

import useModalStore from '@features/modal/model'
import { ModalKey } from '@features/modal/types'

import { cn } from '@shared/lib/cn'
import ButtonClose from '@shared/ui/button-close'

import styles from './styles.module.scss'

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3, ease: easeOut } },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: easeIn },
  },
}

const ModalRoot: React.FC = () => {
  const { modals, closeModal } = useModalStore()

  const closeLastModal = () => {
    const lastKey = Array.from(modals.entries()).splice(-1, 1)[0][0]
    const config = modals.get(lastKey)?.config

    if (config?.onClose) {
      config.onClose()
    }

    closeModal(lastKey)
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLastModal()
    }
  }

  useEventListener('keydown', handleKeydown)

  return (
    <>
      <AnimatePresence>
        {Array.from(modals.entries())
          .filter(([ , modal ]) => modal?.isOpen)
          .map(([ key, modal ]) => {
            const { content, config } = modal!
            const {
              backdropTopOffset = 0,
              topOffset,
              classes = {},
              disabledScroll,
              title,
              description,
              hideIconClose,
              onClose,
            } = config || {}

            return (
              <div key={key}>
                <motion.div
                  className={cn(styles.overlay, {
                    [styles._show]: modal?.isOpen,
                    [styles._blur]: key === 'isAdult',
                  })}
                  style={{
                    top:`${backdropTopOffset}px`,
                    height:`calc(100% - ${backdropTopOffset}px)`
                  }}
                  onClick={() => closeModal(key)}
                  initial="hidden"
                  animate={modal?.isOpen ? 'visible' : 'exit'}
                  exit="exit"
                  variants={modalVariants}
                />
                <motion.div
                  className={cn(styles.modal, 'modal', classes?.modal, {
                    [styles['disabled-scroll']]: disabledScroll,
                  })}
                  initial="hidden"
                  animate={modal?.isOpen ? 'visible' : 'exit'}
                  exit="exit"
                  variants={modalVariants}
                >

                  <div
                    className={cn(styles.body, classes?.body)}
                    style={{
                      top: topOffset ? `${topOffset}px` : undefined,
                      transform: topOffset ? 'translateX(-50%)' : undefined,
                      left: topOffset ? '50%' : undefined,
                      alignItems: topOffset ? 'flex-start' : undefined,
                      justifyContent: topOffset ? 'flex-start' : undefined,
                    }}
                  >
                    <div className={cn(styles.content, classes?.content)}>
                      <div className={styles.top}>
                        {title && (
                          <h3 className={styles.title}>{title}</h3>
                        )}

                        {!hideIconClose && (
                          <ButtonClose
                            className={styles.close}
                            theme="grey"
                            onClick={() => {
                              closeModal(key as ModalKey)

                              if (onClose) {
                                onClose()
                              }
                            }}
                          />
                        )}
                      </div>

                      {description && (
                        <p className={styles.description}>
                          {description}
                        </p>
                      )}

                      {content}
                    </div>
                  </div>
                </motion.div>
              </div>
            )
          })}
      </AnimatePresence>
    </>
  )
}

export default ModalRoot
